import { useState } from "react";
import { magicLinkRequestSchema } from "@shared/schemas";
import { useApp } from "../context/AppContext";

type Props = {
  subtitle?: React.ReactNode;
  ctaLabel?: string;
};

/**
 * Email-input form that requests a magic link. Used by both the SignInPanel
 * modal (header) and the AuthExpiredPage so the validation + submit logic
 * lives in exactly one place.
 */
export function SignInForm({ subtitle, ctaLabel }: Props) {
  const { requestSignIn } = useApp();
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "submitting" | "sent" | "error">("idle");
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const parsed = magicLinkRequestSchema.safeParse({ email });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? "Invalid email");
      setStatus("error");
      return;
    }
    setStatus("submitting");
    setError(null);
    try {
      await requestSignIn(parsed.data.email);
      setStatus("sent");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong");
      setStatus("error");
    }
  };

  if (status === "sent") {
    return (
      <p className="serif-it" style={{ color: "var(--ink-soft)" }}>
        Check your inbox — we just sent a magic link to <b style={{ color: "var(--ink)" }}>{email}</b>. Click it to finish signing in.
      </p>
    );
  }

  return (
    <form onSubmit={onSubmit}>
      {subtitle && (
        <p className="serif-it" style={{ color: "var(--ink-soft)", marginTop: 0 }}>
          {subtitle}
        </p>
      )}
      <label
        className="mono up"
        style={{ display: "block", fontSize: 11, color: "var(--ink-soft)", marginTop: 32, marginBottom: -4 }}
      >
        Email
      </label>
      <input
        className="text-input"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        type="email"
        autoFocus
        placeholder="you@home.com"
        style={{ width: "100%", marginBottom: 16, fontSize: 18, letterSpacing: 0 }}
      />
      {error && (
        <div className="mono" style={{ color: "var(--rust)", fontSize: 12, marginBottom: 12 }}>
          {error}
        </div>
      )}
      <button
        type="submit"
        className="btn btn-rust"
        disabled={status === "submitting"}
        style={{ width: "100%", justifyContent: "center" }}
      >
        {status === "submitting" ? "Sending…" : (ctaLabel ?? "Send me a sign-in link")} <span className="arr">→</span>
      </button>
    </form>
  );
}
